import React, { useContext, useEffect, useRef, useState } from "react";
import {
  IconButton,
  Button
} from "@mui/material";
import {
  Close,
  OpenInFull
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import { v4 as uuidv4 } from "uuid";
import { useChatbot } from "../chatBotContext";
import axios from "axios";

import { useAppTheme } from "core/contexts/theme/theme";
import FullHistoryPanel from "../fullChatConvoHistory";
import { fetchBaseUrl } from "core/utils/common";
import { AuthContext } from "core/contexts/auth/auth";

// Import components
import ConversationSidebar from "./ConversationSidebar";
import MessageList from "./MessageList";
import ChatInput from "./ChatInput";
import TaskSelector from "./TaskSelector";

// Import types
import { Message, Conversation, Task, MessageType } from "./types";

const baseURL = fetchBaseUrl({ port: 8001 });

// Helper functions for date grouping
const formatDate = (date) => new Date(date);

const isToday = (date) => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const isYesterday = (date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
};

const isPastWeek = (date) => {
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  return date > oneWeekAgo && !isToday(date) && !isYesterday(date);
};

const isPastMonth = (date) => {
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  return date > oneMonthAgo && !isPastWeek(date);
};

const groupConversationsByDate = (conversations: Conversation[]) => {
  const todayConversations: Conversation[] = [];
  const yesterdayConversations: Conversation[] = [];
  const pastWeekConversations: Conversation[] = [];
  const lastMonthConversations: Conversation[] = [];
  const olderConversations: Conversation[] = [];

  conversations.forEach((conversation) => {
    const conversationDate = formatDate(conversation.updated_at);

    if (isToday(conversationDate)) {
      todayConversations.push(conversation);
    } else if (isYesterday(conversationDate)) {
      yesterdayConversations.push(conversation);
    } else if (isPastWeek(conversationDate)) {
      pastWeekConversations.push(conversation);
    } else if (isPastMonth(conversationDate)) {
      lastMonthConversations.push(conversation);
    } else {
      olderConversations.push(conversation);
    }
  });

  return {
    todayConversations,
    yesterdayConversations,
    pastWeekConversations,
    lastMonthConversations,
    olderConversations,
  };
};

export default function SearchAgentSuite() {
  const [fullScreen, setFullScreen] = useState(false);
  const [viewMode, setViewMode] = useState("chat");
  const [searchType, setSearchType] = useState("kb");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const { isVisible, hideChatbot } = useChatbot();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [userPrompt, setUserPrompt] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskConversations, setTaskConversations] = useState<Record<string, MessageType[]>>({});

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Conversation history states
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [todayConversations, setTodayConversations] = useState<Conversation[]>([]);
  const [yesterdayConversations, setYesterdayConversations] = useState<Conversation[]>([]);
  const [pastWeekConversations, setPastWeekConversations] = useState<Conversation[]>([]);
  const [lastMonthConversations, setLastMonthConversations] = useState<Conversation[]>([]);
  const [olderConversations, setOlderConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [highlightedConversation, setHighlightedConversation] = useState("");
  const [socket, setSocket] = useState<any>();
  const [taskMessagesMap, setTaskMessagesMap] = useState<{ [taskName: string]: Message[] }>({});
  const [showhistory, setShowhistory] = useState(false);
  const [hidden, setHidden] = useState<{ [id: string]: boolean }>({
    today: false,
    yesterday: true,
    pastWeek: true,
    lastMonth: true,
    older: true
  });

  const { checkAuthToken } = useContext(AuthContext);

  useEffect(() => {
    const fetchTasks = async () => {
      const amsBearerToken = await checkAuthToken();

      try {
        const response = await axios.get(`${baseURL}/ac2/api/tasks`, {
          headers: {
            "X-API-Key": amsBearerToken
          }
        });
        console.log("Tasks fetched:", response.data.tasks);
        setTasks(response.data.tasks);
        const chatTask = response.data.tasks.find((task: Task) => task.name === "chat");
        if (chatTask) {
          setSelectedTask(chatTask);
          setMessages(taskMessagesMap[chatTask.name] || []);
        }
      } catch (err) {
        console.error("Failed to fetch tasks:", err);
      }
    };

    fetchTasks();
  }, []);

  const fetchConversations = async () => {
    try {
      const amsBearerToken = await checkAuthToken();

      console.log("Fetching conversations with token:", amsBearerToken);

      const response = await axios.get(`${baseURL}/ac2/api/context/conversations`, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': amsBearerToken
        }
      });

      console.log('Fetched conversations:', response.data);

      if (response.data && response.data.conversations) {
        setConversations(response.data.conversations);

        // Group conversations by date
        const {
          todayConversations,
          yesterdayConversations,
          pastWeekConversations,
          lastMonthConversations,
          olderConversations
        } = groupConversationsByDate(response.data.conversations);

        setTodayConversations(todayConversations);
        console.log("Today Convos", todayConversations);
        setYesterdayConversations(yesterdayConversations);
        setPastWeekConversations(pastWeekConversations);
        setLastMonthConversations(lastMonthConversations);
        setOlderConversations(olderConversations);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  // Load conversations when component mounts
  useEffect(() => {
    fetchConversations();
  }, []);
  
  // Create a new conversation on the server
  const createNewConversation = async (newConversation: Conversation) => {
    try {
      const amsBearerToken = await checkAuthToken();
      
      // API call to create a conversation
      await axios.post(
        `${baseURL}/ac2/api/context/conversations`, 
        {
          conversation_id: newConversation.id,
          title: newConversation.title
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': amsBearerToken
          }
        }
      );
      
      console.log('Created new conversation with ID:', newConversation.id);
    } catch (error) {
      console.error('Error creating new conversation:', error);
    }
  };

  const allowedTasks = ["chat", "knowledge_base_search", "datalake_chat"];
  const filteredTasks = tasks.filter((task): task is Task => allowedTasks.includes(task.name));

  const handleLabelClick = (label) => {
    setSelectedLabel(label === selectedLabel ? null : label);

    // If clicking on a time period category, toggle its visibility
    if (['today', 'yesterday', 'pastWeek', 'lastMonth', 'older'].includes(label)) {
      setHidden(prev => ({
        ...prev,
        [label]: !prev[label]
      }));
    }

    if (label === "refresh") {
      fetchConversations();
      setIsRefreshing(true);
      setTimeout(() => {
        setIsRefreshing(false);
      }, 2000);
    }

    if (label === "new") {
      setMessages([]);
      setShowhistory(false);
      const newId = uuidv4();
      setConversationId(newId);
      
      // Create a new conversation entry and add it to the today conversations list
      const newConversation: Conversation = {
        id: newId,
        title: "New Conversation",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        user_id: null,
        is_active: true,
        message_count: 0,
        metadata: {}
      };
      
      // Update UI immediately
      setTodayConversations(prev => [newConversation, ...prev]);
      setSelectedConversation(newId);
      setHighlightedConversation(newId);
      
      // Create the conversation on the server
      createNewConversation(newConversation);
    }
  };

  const convoState = useAppSelector((state) => state.chatConversations);

  useEffect(() => {
    if (convoState.current) {
      setHighlightedConversation(convoState.current)
    }
  }, [convoState.current]);

  // Add this function inside your component
  const fetchConversationMessages = async (conversationId: string) => {
    try {
      const amsBearerToken = await checkAuthToken();

      const response = await axios.get(
        `${baseURL}/ac2/api/conversation/${conversationId}/messages`,
        {
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': amsBearerToken
          }
        }
      );

      // Assuming response.data.messages is an array of messages
      // Map them to your Message type if needed
      const dateNow = Date.now();
      const backendMessages = (response.data.messages || []).map((msg: any) => ({
        id: msg.id || `msg-${dateNow}`,
        type: msg.role === "user" ? "user" : "ai",
        content: msg.content,
        timestamp: new Date(msg.timestamp)
      }));

      setMessages(backendMessages);
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
      setMessages([
        {
          id: `error-${Date.now()}`,
          type: 'ai',
          content: 'Failed to load messages for this conversation.',
          timestamp: new Date()
        }
      ]);
    }
  };

  // Replace your current handleSelectConversation with this:
  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation.id);
    fetchConversationMessages(conversation.id);
    setHighlightedConversation(conversation.id);
  };

  const handleCloseAgent = () => {
    hideChatbot();
  }

  const [conversationId, setConversationId] = useState<string | null>(null);

  useEffect(() => {
    if (!conversationId) {
      setConversationId(uuidv4());
    }
  }, []);

  const postData = async () => {
    if (!userPrompt.trim()) return;

    // Add user message to the conversation
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: userPrompt,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    // Clear input field
    setUserPrompt('');

    // Set loading state
    setIsLoading(true);

    try {
      const amsBearerToken = await checkAuthToken();

      const endpoint = selectedTask?.name !== "chat"
        ? `${baseURL}/ac2/api/task/${selectedTask?.name}?stream=true`
        : `${baseURL}/ac2/api/serve?stream=true`;

      const response = await axios.post(
        endpoint,
        {
          user_input: userMessage.content,
          conversation_id: conversationId
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': amsBearerToken
          }
        }
      );

      // Get the response data
      const responseData = response.data?.response || response.data;
      console.log('Success:', response.data);
      console.log('Response content:', responseData);

      // Add AI response to conversation
      const aiMessage: Message = {
        id: `ai-${Date.now()}`,
        type: 'ai',
        content: responseData || 'No response received from the server',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);

      // End loading state
      setIsLoading(false);
    } catch (error) {
      console.error('Error:', error);

      // Add error message to conversation
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        type: 'ai',
        content: 'Sorry, there was an error processing your request.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);

      // End loading state
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTask) {
      setMessages(taskMessagesMap[selectedTask.name] || []);
    }
  }, [selectedTask]);

  const { apptheme, toggleTheme } = useAppTheme();
  
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleVoiceInput = (text: string) => {
    setUserPrompt(text);
  };

  const sendMessageByVoice = (data) => {
    postData();
  }

  if (!isVisible) return null;
  
  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${apptheme.name === "dark-theme" ? "backdrop-blur-md bg-gray-700 bg-opacity-10" : "backdrop-blur-md bg-purple-300 bg-opacity-40"}`}>
      <div
        className={`border border-black shadow-lg transition-all duration-300 ease-in-out ${fullScreen ? "w-screen h-screen" : "w-[90vw] h-[89vh]"} flex`}
      >
        {/* Sidebar */}
        <ConversationSidebar 
          apptheme={apptheme}
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
          handleLabelClick={handleLabelClick}
          isRefreshing={isRefreshing}
          setShowhistory={setShowhistory}
          hidden={hidden}
          todayConversations={todayConversations}
          yesterdayConversations={yesterdayConversations}
          pastWeekConversations={pastWeekConversations}
          lastMonthConversations={lastMonthConversations}
          olderConversations={olderConversations}
          selectedConversation={selectedConversation}
          handleSelectConversation={handleSelectConversation}
          fullScreen={fullScreen}
        />

        {/* Main Content */}
        <div className={`flex-1 flex flex-col ${apptheme.name === "dark-theme" ? "bg-[#1d1d1d]" : "bg-[#faf5ff]"} overflow-hidden`}>
          {/* Header */}
          <div className={`flex justify-end ${apptheme.name === "dark-theme" ? "bg-[#460073] !important" : "bg-[#faf5ff]"} px-6 pt-3 pb-3`}>
            <div className="space-x-3 flex items-center">
              <IconButton onClick={() => setFullScreen(!fullScreen)}>
                <OpenInFull className="text-purple-600" />
              </IconButton>
              <IconButton onClick={handleCloseAgent}>
                <Close className="text-purple-600" />
              </IconButton>
            </div>
          </div>

          {/* Body */}
          {viewMode === "chat" && (
            <div className={`flex-1 px-6 pb-3 ${!selectedTask ? "overflow-y-auto" : "overflow-y-auto"}`}>
              {showhistory ? (
                <FullHistoryPanel
                  isOpen={showhistory}
                  onClose={() => setShowhistory(false)}
                  conversations={conversations}
                  apptheme={apptheme}
                  onSelectConversation={fetchConversationMessages}
                  fetchConversations={fetchConversations}
                />
              ) : (
                <>
                  <TaskSelector 
                    apptheme={apptheme}
                    filteredTasks={filteredTasks}
                    selectedTask={selectedTask}
                    setSelectedTask={setSelectedTask}
                    setMessages={setMessages}
                    taskMessagesMap={taskMessagesMap}
                  />

                  {/* Show messages for the selected task */}
                  {selectedTask && (
                    <div className="p-3 z-1">
                      <MessageList 
                        messages={messages}
                        isLoading={isLoading}
                        apptheme={apptheme}
                        messagesEndRef={messagesEndRef}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Footer Chat Input */}
          {!showhistory && (
            <ChatInput 
              apptheme={apptheme}
              userPrompt={userPrompt}
              setUserPrompt={setUserPrompt}
              postData={postData}
              handleVoiceInput={handleVoiceInput}
              sendMessageByVoice={sendMessageByVoice}
            />
          )}
          
          <div className={`text-right mt-3 ${apptheme.name === "dark-theme" ? "bg-[#460073] !important" : "bg-[#F4EDF9]"}`}>
            <Button variant="text" color="primary" onClick={handleCloseAgent}>
              Close Agent Suite
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}